import React, { useCallback, useRef } from 'react';
import { Image, Typography } from 'lib/components';
import ReactDatePicker from 'react-datepicker';
import classes from './index.module.scss';

/**
 * Props interface for the CustomDatePicker component
 */
interface Props {
	/** Currently selected date value */
	value: Date;
	/** Callback function when date changes */
	onChange: (value: Date) => any;
	/** Label text for the date picker */
	label: string;
	/** Whether to disable the dropdown calendar on icon click */
	disableDropdown?: boolean;
}

/**
 * CustomDatePicker component
 * Renders a styled date picker with calendar icon
 * Provides a more customized appearance than the standard DatePicker
 *
 * @param {Props} props - Component props
 * @returns {JSX.Element} - Rendered component
 */
const CustomDatePicker = ({ value, onChange, label, disableDropdown = false }: Props) => {
	// Reference to the underlying ReactDatePicker component
	const reactDatePickerRef = useRef<ReactDatePicker>(null);

	/**
	 * Show the date picker dropdown when calendar icon is clicked
	 * Only works if dropdown is not disabled
	 */
	const showDropdown = useCallback(() => {
		if (!disableDropdown) {
			reactDatePickerRef.current?.setOpen(true);
		}
	}, [disableDropdown]);

	return (
		<div className={classes['datePicker']}>
			{/* Date picker label */}
			<Typography className={classes['datePicker-label']}>{label}:</Typography>
			<div className={classes['datePicker-fieldArea']}>
				{/* Calendar icon that opens the dropdown */}
				<Image onClick={showDropdown} src="icCalendar" />
				{/* React DatePicker component with custom styling */}
				<ReactDatePicker
					ref={reactDatePickerRef}
					className={classes['datePicker-reactDatePicker']}
					selected={value}
					onChange={(date: Date) => date && onChange(date)}
				/>
			</div>
		</div>
	);
};

export default CustomDatePicker;
